import { dispatch, select, useDispatch } from '@wordpress/data';
import { PluginBlockSettingsMenuItem } from '@wordpress/edit-post';
import { __ } from '@wordpress/i18n';
import { store as keyboardShortcutsStore, useShortcut } from '@wordpress/keyboard-shortcuts';
import { registerPlugin } from '@wordpress/plugins';

import { useEffect } from '@wordpress/element';
import excludeKeys from './excludes.js';
import SettingsIcons from './icons.js';
import getProgressmatifyEditorLocalStorage from './local-storage.js';

const NXBCopyPasteStyles = () => {
    // Registering the shortcuts
    const { registerShortcut } = useDispatch(keyboardShortcutsStore);

    const { hasMultiSelection } = select('core/block-editor');
    const progressmatifyCopyPasteStylesSetter = getProgressmatifyEditorLocalStorage();

    useEffect(() => {
        const progressmatifyCopyPasteStyles = getProgressmatifyEditorLocalStorage('progressmatifyCopyPasteStyles');

        if (!progressmatifyCopyPasteStyles) {
            progressmatifyCopyPasteStylesSetter.setItem('progressmatifyCopyPasteStyles', JSON.stringify({}));
        }

        if (progressmatifyCopyPasteStyles) {
            for (const block in progressmatifyCopyPasteStyles) {
                const hoursSinceStylesSaved = Math.abs(Date.now() - progressmatifyCopyPasteStyles[block].stylesSavedTimeStamp) / 36e5;

                if (hoursSinceStylesSaved >= 8) {
                    delete progressmatifyCopyPasteStyles[block];
                }
            }

            progressmatifyCopyPasteStylesSetter.setItem('progressmatifyCopyPasteStyles', JSON.stringify(progressmatifyCopyPasteStyles));
        }

        registerShortcut({
            name: 'progressmatify/copy',
            category: 'block',
            description: __('Copy the selected block(s).', 'progressmatify-blocks'),
            keyCombination: {
                modifier: 'primaryShift',
                character: 'y'
            }
        });

        registerShortcut({
            name: 'progressmatify/paste',
            category: 'block',
            description: __('Paste the selected block(s).', 'progressmatify-blocks'),
            keyCombination: {
                modifier: 'primaryShift',
                character: 'u'
            }
        });
    }, []);

    const copyStylesHandler = () => {
        // eslint-disable-next-line no-shadow
        const { getSelectedBlock, hasMultiSelection, getMultiSelectedBlocks } = select('core/block-editor');

        if (hasMultiSelection()) {
            const multiSelectedBlocksData = getMultiSelectedBlocks();
            multiSelectedBlocksData.map(block => {
                if (block) {
                    storeBlockStyles(block);
                }

                return block;
            });

            return;
        }

        const selectedBlockData = getSelectedBlock();

        if (selectedBlockData) {
            storeBlockStyles(selectedBlockData);
        }
    };

    const pasteStylesHandler = () => {
        // eslint-disable-next-line no-shadow
        const { getSelectedBlock, hasMultiSelection, getMultiSelectedBlocks } = select('core/block-editor');

        if (hasMultiSelection()) {
            const multiSelectedBlocksData = getMultiSelectedBlocks();
            multiSelectedBlocksData.map(block => {
                if (block) {
                    pasteBlockStyles(block);
                }

                return block;
            });

            return;
        }

        const selectedBlockData = getSelectedBlock();

        if (selectedBlockData) {
            pasteBlockStyles(selectedBlockData);
        }
    };

    const storeBlockStyles = blockData => {
        const progressmatifyCopyPasteStyles = getProgressmatifyEditorLocalStorage('progressmatifyCopyPasteStyles');
        progressmatifyCopyPasteStylesSetter.setItem('progressmatifyCopyPasteStyles', JSON.stringify({}));

        const { attributes, name, innerBlocks } = blockData;

        if (progressmatifyCopyPasteStyles) {
            progressmatifyCopyPasteStylesSetter.setItem('progressmatifyCopyPasteStyles', JSON.stringify({}));
        }

        let styles = {};
        const parentStyle = {};

        if (name.includes('progressmatify/')) {
            const blockName = name.replace('progressmatify/', '');

            let blockAttributes = attributes;

            progressmatifyCopyPasteStyles[`${blockName}-styles`] = {};
            progressmatifyCopyPasteStyles[`global-style`] = {};

            if (blockAttributes && progressmatifyCopyPasteStyles) {
                Object.keys(blockAttributes).map(attribute => {
                    let key = attribute;

                    if (
                        undefined !== attributes[attribute] &&
                        null !== attributes[attribute] &&
                        (key.includes('progressmatify_') || key.includes('aff_')) &&
                        !excludeKeys.includes(key)
                    ) {
                        styles[key] = attributes[attribute];
                        parentStyle[attribute] = attributes[attribute];
                    }

                    return attribute;
                });
            }
            if (innerBlocks) {
                parentStyle.innerblocks = innerBlocks;
            }

            styles.stylesSavedTimeStamp = Date.now();

            progressmatifyCopyPasteStyles[`${blockName}-styles`] = parentStyle;
            progressmatifyCopyPasteStyles[`global-style`] = styles;

            progressmatifyCopyPasteStylesSetter.setItem('progressmatifyCopyPasteStyles', JSON.stringify(progressmatifyCopyPasteStyles));
        }
    };

    const pasteBlockStyles = blockData => {
        const { name, clientId, innerBlocks } = blockData;

        let styles;
        let pasteStyle;
        const parentAttr = {};

        const progressmatifyCopyPasteStyles = getProgressmatifyEditorLocalStorage('progressmatifyCopyPasteStyles');

        if (name.includes('progressmatify/')) {
            styles = progressmatifyCopyPasteStyles[`global-style`];

            const blockName = name.replace('progressmatify/', '');
            const blockAttributes = blockData.attributes;

            pasteStyle = progressmatifyCopyPasteStyles[`${blockName}-styles`];

            if (blockAttributes && pasteStyle) {
                updateBlockStyles(clientId, pasteStyle);

                if (innerBlocks) {
                    const childAttr = {};

                    innerBlocks.map((childBlock, index) => {
                        const childName = childBlock.name.replace('progressmatify/', '');
                        const blockAttributes = childBlock.attributes;

                        if (pasteStyle.innerblocks[index].name === 'progressmatify/' + childName) {
                            Object.keys(blockAttributes).map(attribute => {
                                if (attribute.includes('progressmatify_') && !excludeKeys.includes(attribute)) {
                                    childAttr[attribute] = pasteStyle.innerblocks[index].attributes[attribute];
                                }

                                return childAttr;
                            });
                        }
                        updateBlockStyles(childBlock.clientId, childAttr);

                        return childBlock;
                    });
                }
            } else if (blockAttributes && styles) {
                Object.keys(blockAttributes).map(attribute => {
                    const key = attribute;

                    Object.keys(styles).map(item => {
                        if (item === key && key.includes('progressmatify_') && !excludeKeys.includes(key)) {
                            parentAttr[attribute] = styles[key];
                        }

                        return parentAttr;
                    });

                    return parentAttr;
                });

                updateBlockStyles(clientId, parentAttr);
            }
        }
    };

    const updateBlockStyles = (clientId, styles) => {
        dispatch('core/block-editor').updateBlockAttributes(clientId, styles);
    };

    useShortcut('progressmatify/copy', event => {
        copyStylesHandler();
        event.preventDefault();
    });
    useShortcut('progressmatify/paste', event => {
        pasteStylesHandler();
        event.preventDefault();
    });

    if (hasMultiSelection()) {
        stylesText = __('Styles', 'progressmatify-blocks');
    }

    return (
        <>
            <PluginBlockSettingsMenuItem
                icon={SettingsIcons.copy}
                label={__('Progressmatify Copy Styles', 'progressmatify-blocks')}
                onClick={copyStylesHandler}
            />
            <PluginBlockSettingsMenuItem
                icon={SettingsIcons.paste}
                label={__('Progressmatify Paste Styles', 'progressmatify-blocks')}
                onClick={pasteStylesHandler}
            />
        </>
    );
};

registerPlugin('progressmatify-block-copy-paste', {
    render: NXBCopyPasteStyles
});
